<form [formGroup]="patientForm">
  <div fxLayout="row" fxLayout.xs="column" fxLayoutAlign="space-between center">
    <h2 mat-dialog-title>Load A New Patient</h2>
  </div>

  <mat-horizontal-stepper linear #stepper>
    <!-- Step 1: Client & Basic Information -->
    <mat-step [stepControl]="patientForm" label="Client & Basic Info">
      <div>
        <div class="patient-row">
          <mat-form-field>
            <mat-label>Client</mat-label>
            <mat-select formControlName="client" [compareWith]="compareClientObjects">
              <mat-option *ngFor="let client of clients$ | async" [value]="client">
                {{ client.client_name }}
              </mat-option>
            </mat-select>
            <mat-error>You must assign a client</mat-error>
          </mat-form-field>
          <mat-form-field>
            <mat-label>HubSpot Id</mat-label>
            <input placeholder="HubSpot ID" matInput formControlName="hubspot_id" />
          </mat-form-field>
        </div>
        <div class="patient-row">
          <mat-form-field>
            <mat-label>First Name</mat-label>
            <input placeholder="First Name" matInput formControlName="firstName" />
            <mat-error>First Name is required</mat-error>
          </mat-form-field>

          <mat-form-field>
            <mat-label>Last Name</mat-label>
            <input placeholder="Last Name" matInput formControlName="lastName" />
            <mat-error>Last Name is required</mat-error>
          </mat-form-field>
        </div>
        <div class="patient-row">
          <mat-form-field>
            <mat-label>Middle Name</mat-label>
            <input placeholder="Middle Name (Optional)" matInput formControlName="middleName" />
          </mat-form-field>
          <mat-form-field>
            <input
              matInput
              formControlName="dob"
              [max]="setMaxDate()"
              [matDatepicker]="picker"
              placeholder="Date of Birth"
            />
            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
            <mat-error>Valid DOB required</mat-error>
          </mat-form-field>
        </div>
        <div class="patient-row">
          <mat-form-field>
            <mat-label>Patient Email (optional)</mat-label>
            <input placeholder="Email is required for patient login" matInput formControlName="email" />
          </mat-form-field>
          <app-language-selector
            [languageInput]="patientForm.get('language')"
            [languageModel]="languageModel"
          ></app-language-selector>
        </div>
        
        <div class="patient-row">
        
          <!-- MRN (shared component) -->
          <app-mrn-input [control]="patientForm.get('emr_id')" [currentUserId]="null" appearance="standard"></app-mrn-input>
        
          <mat-form-field>
            <mat-label>SSN (optional)</mat-label>
            <input placeholder="Last 4 Social" matInput formControlName="ssn_id" />
          </mat-form-field>
        </div>
        
        <div class="step-navigation">
          <button mat-raised-button color="primary" (click)="onNextFromBasicInfo(stepper)">
            Next
          </button>
        </div>
        </div>
        </mat-step>

    <!-- Step 2: Providers & Timezone -->
    <mat-step [stepControl]="patientForm" [completed]="isStepValid(1)" label="Providers & Timezone">
      <div>
        <div class="patient-row full-width">
          <div class="patient-row patient-providers" fxLayout="column" fxLayoutAlign="flex-start start">
            <h3 class="patient-subtitle">Providers</h3>
            <ul *ngIf="providers?.length > 0">
              <li class="patient-provider" matTooltip="{{ provider.email }}" *ngFor="let provider of providers">
                {{ provider.name }}- {{ provider.role }}
                <mat-chip-list *ngIf="provider.rank === 1" class="small-mat-chip">
                  <mat-chip color="primary" matTooltip="Primary provider" selected>Primary</mat-chip>
                </mat-chip-list>
              </li>
            </ul>
            <a (click)="openProviderPanel()" color="accent"><span class="patient-open-dialog">Add providers</span></a>
          </div>
        </div>
        <div class="patient-row">
          <div class="patient-selectors" fxFill flexLayout="column" flexLayoutAlign="flex-start start">
            <h3>Select Patient Zone</h3>
            <app-timezone-picker
              [customPlaceholderText]="'Patient Zone'"
              [formControlName]="'timezone'"
              [config]="timezoneConfig"
            >
            </app-timezone-picker>
          </div>
        </div>
        <div class="step-navigation">
          <button mat-raised-button matStepperPrevious>Back</button>
          <button mat-raised-button matStepperNext color="primary">Next</button>
        </div>
      </div>
    </mat-step>

    <!-- Step 3: Programs & Notes -->
    <mat-step [stepControl]="patientForm" [completed]="isStepValid(2)" label="Programs & Notes">
      <div>
        <div>
          <h3>Select Programs</h3>
          <ng-container>
            <div class="basicinfo-form-row">
              <mat-form-field appearance="legacy">
                <input
                  class="search-patient-input"
                  type="text"
                  placeholder="Add program"
                  aria-describedby="Add program to patient"
                  matInput
                  formControlName="programInput"
                  [matAutocomplete]="auto"
                />
                <mat-autocomplete #auto="matAutocomplete">
                  <mat-option
                    *ngFor="let program of filteredProgramOptions$ | async"
                    [value]="program?.value"
                    (click)="selectProgram(program.value)"
                  >
                    <span>{{ program?.name }} ({{ program.value?.toUpperCase() }})</span>
                  </mat-option>
                </mat-autocomplete>
              </mat-form-field>
            </div>
            <div class="basicinfo-form-row">
              <mat-form-field class="chip-list" appearance="fill">
                <mat-chip-list #chipList placeholder="Add programs">
                  <mat-chip
                    *ngFor="let program of programs"
                    [selectable]="true"
                    [removable]="true"
                    matTooltip="{{ program.name }}"
                    (removed)="removeProgram(program.value)"
                    selected
                  >
                    {{ program?.value?.toUpperCase() }}
                    <mat-icon matChipRemove>cancel</mat-icon>
                  </mat-chip>
                </mat-chip-list>
              </mat-form-field>
            </div>
            <p class="noProgramsPar" *ngIf="patientForm.get('status')?.errors?.noPrograms">Program is required</p>
            <p class="noProgramsPar" *ngIf="patientForm.get('status')?.errors?.apcmConflict">
              APCM can't be selected with other CCM, PCM or TCM programs
            </p>
            <ng-container *ngIf="patientForm?.get('status')?.get('apcm')?.value">
              <div class="basicinfo-form-row">
                <mat-form-field appearance="outline" formGroupName="status">
                  <mat-label>APCM level</mat-label>
                  <mat-select formControlName="tier_apcm">
                    <mat-option *ngFor="let tier of apcmTiers" [value]="tier">
                      {{ tier.name }}
                    </mat-option>
                  </mat-select>
                </mat-form-field>
              </div>
            </ng-container>
          </ng-container>
        </div>
        <div class="patient-row">
          <div fxFill flexLayout="column" flexLayoutAlign="flex-start start">
            <h3>Notes (optional)</h3>
            <mat-form-field class="full-width" appearance="outline">
              <textarea placeholder="Notes" matInput formControlName="notes" rows="4"></textarea>
            </mat-form-field>
          </div>
        </div>
        <div class="step-navigation">
          <button mat-raised-button matStepperPrevious>Back</button>
          <button mat-raised-button matStepperNext color="primary">Next</button>
        </div>
      </div>
    </mat-step>

    <!-- Step 4: Review & Submit -->
    <mat-step label="Review & Submit" [completed]="isStepValid(3)">
      <div class="review-section-container">
        <h3>Review Patient Information</h3>
        <div class="review-section">
          <h4>Client & Basic Information</h4>
          <p><strong>Client:</strong> {{ patientForm.get('client').value?.client_name }}</p>
          <p>
            <strong>Name:</strong> {{ patientForm.get('firstName').value }} {{ patientForm.get('middleName').value }}
            {{ patientForm.get('lastName').value }}
          </p>
          <p><strong>Date of Birth:</strong> {{ patientForm.get('dob').value | date }}</p>
          <p><strong>Email:</strong> {{ patientForm.get('email').value || 'Not provided' }}</p>
          <p><strong>Language:</strong> {{ patientForm.get('language').value }}</p>
          <p><strong>MRN:</strong> {{ patientForm.get('emr_id').value }}</p>
        </div>

        <div class="review-section">
          <h4>Providers</h4>
          <p *ngFor="let provider of providers">
            <strong>{{ provider.name }}</strong>
          </p>
          <h4>Timezone</h4>
          <p><strong>Timezone:</strong> {{ patientForm.get('timezone').value?.name }}</p>
        </div>

        <div class="review-section">
          <h4>Programs</h4>
          <p *ngFor="let program of programs">
            <strong>{{ program.name }}</strong>
          </p>
        </div>

        <div class="review-section" formGroupName="status">
          <h4>Consents</h4>
          <p>Patient agreed to informed consent of the following programs:</p>
          <mat-checkbox formControlName="consent_{{ program.value }}" *ngFor="let program of programs">
            {{ program.name }}.</mat-checkbox
          >
        </div>

        <div class="step-navigation">
          <button mat-raised-button matStepperPrevious>Back</button>
          <button
            mat-raised-button
            color="primary"
            (click)="loadPatientToDatabase()"
            [disabled]="patientForm.invalid || isLoading || !isStepValid(3)"
            cdkFocusInitial
          >
            Load Patient
          </button>
          <button mat-raised-button (click)="onNoClick()">Cancel</button>
        </div>
      </div>
    </mat-step>
  </mat-horizontal-stepper>

  <mat-progress-bar mode="indeterminate" *ngIf="isLoading" style="padding-top: 10px"></mat-progress-bar>
</form>
